/**
 * Form Utilities — helpers for TanStack Form validation
 */
import { toast } from "sonner";

/**
 * Kiểm tra field có lỗi validation hay không.
 * Dùng trong `className={cn(hasFieldError(field) && 'border-destructive')}`
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasFieldError(field: { state: { meta: { errors: any[] } } }): boolean {
    return field.state.meta.errors && field.state.meta.errors.length > 0;
}

/**
 * Map tên field kỹ thuật sang nhãn tiếng Việt thân thiện
 */
const FIELD_LABELS: Record<string, string> = {
    customerId: 'Khách hàng',
    customerName: 'Tên khách hàng',
    productName: 'Tên sản phẩm',
    productCode: 'Mã sản phẩm',
    typeId: 'Loại sản phẩm',
    unit: 'Đơn vị tính',
    quantity: 'Số lượng',
    priceVND: 'Đơn giá VND',
    quotedPrice: 'Giá báo chốt',
    contactPerson: 'Người liên hệ',
    phone: 'Số điện thoại',
    email: 'Email',
    deliveryAddress: 'Địa chỉ giao hàng',
    createdDate: 'Ngày tạo',
    validUntil: 'Ngày hiệu lực',
    notes: 'Ghi chú',
    specs: 'Quy cách',
    materialId: 'Vật tư',
    processId: 'Công đoạn',
    note: 'Ghi chú',
    length: 'Chiều dài',
    width: 'Chiều rộng',
    height: 'Chiều cao',
    price: 'Giá bán',
    costPrice: 'Giá vốn',
    lines: 'Dòng hàng',
    itemId: 'Sản phẩm',
    itemType: 'Loại sản phẩm',
    itemCode: 'Mã sản phẩm',
    itemName: 'Tên sản phẩm',
    sourceWarehouseId: 'Kho xuất',
    targetWarehouseId: 'Kho nhận',
    vendorId: 'Nhà cung cấp',
    orderDetails: 'Chi tiết đơn hàng',
    unitPrice: 'Đơn giá',
    details: 'Danh sách sản phẩm',
};

/**
 * Dịch tên field path sang nhãn tiếng Việt.
 * VD: "productMaterials[0].note" -> "Vật tư [1] > Ghi chú"
 */
function humanizeFieldPath(path: string): string {
    return path
        .replace(/\[(\d+)\]/g, (_, idx) => ` [${Number(idx) + 1}]`)
        .split('.')
        .map(segment => {
            const clean = segment.replace(/ \[\d+\]/, '');
            const idx = segment.match(/\[(\d+)\]/)?.[0] || '';
            return (FIELD_LABELS[clean] || clean) + idx;
        })
        .join(' > ');
}

/**
 * Trích xuất các câu thông báo lỗi thân thiện tiếng Việt từ bất kỳ cấu trúc lỗi nào của Zod / TanStack Form.
 * Lọc bỏ "Invalid input" chung chung — thay bằng nhãn field cụ thể.
 */
export function extractCleanErrorMessage(err: unknown, fieldPath?: string): string[] {
    if (!err) return [];
    if (typeof err === 'string') {
        const trimmed = err.trim();
        // Bỏ qua "Invalid input" chung chung — nó không giúp user biết lỗi ở đâu
        if (trimmed === 'Invalid input' || trimmed === 'invalid_union' || trimmed === 'invalid_type') {
            if (fieldPath) {
                return [`${humanizeFieldPath(fieldPath)}: giá trị không hợp lệ`];
            }
            return [];
        }
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                const parsed = JSON.parse(trimmed);
                return extractCleanErrorMessage(parsed, fieldPath);
            } catch {
                return [trimmed];
            }
        }
        return [trimmed];
    }
    if (Array.isArray(err)) {
        return err.flatMap(item => extractCleanErrorMessage(item, fieldPath));
    }
    if (typeof err === 'object' && err !== null) {
        const obj = err as Record<string, unknown>;

        // Zod error object with path — trích xuất path để hiện tên field
        if (Array.isArray(obj.path) && obj.path.length > 0) {
            const zodPath = (obj.path as (string | number)[]).map(String).join('.');
            const msg = typeof obj.message === 'string' ? obj.message.trim() : '';
            if (msg && msg !== 'Invalid input' && msg !== 'Required') {
                return [`${humanizeFieldPath(zodPath)}: ${msg}`];
            }
            return [`${humanizeFieldPath(zodPath)}: giá trị không hợp lệ`];
        }

        // Standard { message: "..." }
        if (typeof obj.message === 'string' && obj.message.trim()) {
            const msg = obj.message.trim();
            if (msg === 'Invalid input' && fieldPath) {
                return [`${humanizeFieldPath(fieldPath)}: giá trị không hợp lệ`];
            }
            if (msg !== 'Invalid input') {
                return [msg];
            }
            return [];
        }

        // Zod unionErrors — đào sâu vào issues
        if (Array.isArray(obj.unionErrors)) {
            const issues = (obj.unionErrors as any[]).flatMap(ue => ue?.issues || []);
            if (issues.length > 0) {
                return extractCleanErrorMessage(issues, fieldPath);
            }
        }

        // TanStack Form errorMap: { onChange: { "field.name": [...] } }
        if (obj.onChange || obj.onSubmit || obj.onBlur || obj.onDynamic) {
            const results: string[] = [];
            for (const [validatorKey, fieldErrors] of Object.entries(obj)) {
                if (typeof fieldErrors === 'object' && fieldErrors !== null && !Array.isArray(fieldErrors)) {
                    for (const [fPath, fErr] of Object.entries(fieldErrors as Record<string, unknown>)) {
                        results.push(...extractCleanErrorMessage(fErr, fPath));
                    }
                } else {
                    results.push(...extractCleanErrorMessage(fieldErrors, validatorKey));
                }
            }
            return results;
        }

        return Object.entries(obj).flatMap(([key, val]) => extractCleanErrorMessage(val, key));
    }
    return [String(err)];
}

/**
 * Gom toàn bộ thông báo lỗi (đã Việt hóa, loại trùng) từ state của TanStack Form.
 * Dùng để hiển thị danh sách "còn thiếu gì" thay vì chỉ báo lỗi từng cái một.
 */
export function collectFormErrorMessages(formState: {
    fieldMeta?: Record<string, { errors?: unknown[] }>;
    errorMap?: Record<string, unknown>;
}): string[] {
    const collected: string[] = [];
    const fieldMeta = formState.fieldMeta || {};
    for (const [name, meta] of Object.entries(fieldMeta)) {
        if (meta?.errors && meta.errors.length > 0) {
            collected.push(...extractCleanErrorMessage(meta.errors, name));
        }
    }
    if (formState.errorMap) {
        collected.push(...extractCleanErrorMessage(formState.errorMap));
    }
    return Array.from(new Set(
        collected
            .map((m) => m.trim())
            .filter((m) => m && m !== 'Invalid input' && m !== 'invalid_union'),
    ));
}

/**
 * Focus + scroll vào field lỗi đầu tiên khi submit thất bại,
 * đồng thời hiển thị thông báo toast tiếng Việt rõ ràng, thân thiện (CẤM hiển thị raw JSON).
 * Gọi trong `requestAnimationFrame(() => focusFirstError(form.state))`.
 */
export function focusFirstError(formState: {
    fieldMeta?: Record<string, { errors?: unknown[] }>;
    errorMap?: Record<string, unknown>;
}) {
    let hasFieldErrors = false;
    let firstFocused = false;
    const collectedMessages: string[] = [];

    const fieldMeta = formState.fieldMeta || {};
    for (const [name, meta] of Object.entries(fieldMeta)) {
        if (meta?.errors && meta.errors.length > 0) {
            hasFieldErrors = true;
            collectedMessages.push(...extractCleanErrorMessage(meta.errors, name));

            if (!firstFocused) {
                // Thử focus field lỗi đầu tiên — hỗ trợ cả field trong modal/nested form
                const el = document.getElementById(name)
                    || (document.querySelector(`[name="${name}"]`) as HTMLElement)
                    || (document.querySelector(`[data-field="${name}"]`) as HTMLElement);

                if (el) {
                    firstFocused = true;
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    
                    // Dò tìm phần tử tương tác thực tế (button, input, textarea, select) nếu el là wrapper div
                    const focusTarget = (
                        el.matches('button, input, textarea, select, [tabindex]:not([tabindex="-1"])')
                            ? el
                            : el.querySelector('button, input, textarea, select, [tabindex]:not([tabindex="-1"])')
                    ) as HTMLElement || el;

                    try {
                        focusTarget.focus({ preventScroll: true });
                    } catch {
                        el.focus();
                    }

                    el.classList.add('ring-2', 'ring-destructive', 'ring-offset-2', 'animate-pulse');
                    if (focusTarget !== el) {
                        focusTarget.classList.add('ring-2', 'ring-destructive', 'ring-offset-1');
                    }
                    setTimeout(() => {
                        el.classList.remove('ring-2', 'ring-destructive', 'ring-offset-2', 'animate-pulse');
                        if (focusTarget !== el) {
                            focusTarget.classList.remove('ring-2', 'ring-destructive', 'ring-offset-1');
                        }
                    }, 3000);
                }
            }
        }
    }

    // Trích xuất thêm từ errorMap của form (onSubmit, onChange, onBlur)
    if (formState.errorMap) {
        collectedMessages.push(...extractCleanErrorMessage(formState.errorMap));
    }

    // Lọc bỏ "Invalid input" chung chung còn sót, deduplicate
    const uniqueMessages = Array.from(new Set(
        collectedMessages
            .map(m => m.trim())
            .filter(m => m && m !== 'Invalid input' && m !== 'invalid_union')
    ));

    if (hasFieldErrors || uniqueMessages.length > 0) {
        if (uniqueMessages.length === 0) {
            // Có lỗi nhưng không trích xuất được message cụ thể — vẫn phải báo user
            toast.error("Dữ liệu không hợp lệ. Vui lòng kiểm tra các trường được đánh dấu đỏ.");
        } else if (uniqueMessages.length === 1) {
            toast.error(uniqueMessages[0]);
        } else if (uniqueMessages.length <= 6) {
            // Liệt kê đầy đủ các lỗi để người dùng sửa một lần
            toast.error(uniqueMessages.map((m) => `• ${m}`).join('\n'), { duration: 8000 });
        } else {
            // Quá nhiều lỗi — hiện 6 lỗi đầu + tổng số còn lại
            const shown = uniqueMessages.slice(0, 6).map((m) => `• ${m}`).join('\n');
            toast.error(`${shown}\n... và ${uniqueMessages.length - 6} lỗi khác`, { duration: 10000 });
        }
    }
}

